import React from "react";
import { Star } from "lucide-react";
import { useSiteContent } from "@/contexts/SiteContentContext";
import { HOME } from "@/lib/homeContent";

const K = { fontFamily: "Kanit, Arial, sans-serif" };

// Compact reviews trust signal. Renders NOTHING unless CMS `trust_reviews.visible === "yes"` with a rating and count.
// demo_preview === "on" → demo values, explicit demo badge, no external link, no structured data (never).
export default function TrustSignal({ className = "" }) {
  const { section } = useSiteContent();
  const c = section("trust_reviews", HOME.trust_reviews);
  const rating = parseFloat(c.rating);
  const count = parseInt(c.review_count, 10);
  if (c.visible !== "yes" || !(rating > 0) || !(count > 0)) return null;
  const demo = c.demo_preview === "on";
  const full = Math.round(Math.min(5, rating));
  const label = (c.label || "").replace("{count}", String(count));
  const meta = [label, !demo && c.source ? c.source : null].filter(Boolean).join(" · ");
  const linked = !demo && !!c.source_url;
  const Wrapper = linked ? "a" : "div";
  return (
    <Wrapper
      data-testid="trust-signal"
      data-demo={demo ? "true" : "false"}
      {...(linked ? { href: c.source_url, target: "_blank", rel: "noopener noreferrer" } : {})}
      className={`inline-flex flex-wrap items-center gap-x-3 gap-y-1.5 text-[#451C25] ${linked ? "hover:opacity-80 transition-opacity" : ""} ${className}`}
      aria-label={`${rating.toFixed(1)} out of 5. ${meta}.${demo ? ` ${c.demo_label}.` : ""}`}
    >
      <span className="flex items-center gap-0.5" aria-hidden="true">
        {[0, 1, 2, 3, 4].map((i) => <Star key={i} size={13} className={i < full ? "fill-[#B3A17D] text-[#B3A17D]" : "text-[#B3A17D]/40"} />)}
      </span>
      <span className="text-[13px]" style={{ ...K, fontWeight: 500 }}>{rating.toFixed(1)}</span>
      <span className="text-[12px] text-[#6B4954]" style={K}>{meta}</span>
      {demo && (
        <span data-testid="trust-signal-demo-label" className="text-[10px] tracking-[0.2em] uppercase font-semibold text-[#897756] border border-[#B3A17D]/70 px-2 py-0.5" style={K}>{c.demo_label}</span>
      )}
    </Wrapper>
  );
}
